import { useState, useRef, useEffect, useMemo } from 'react';

import Image from 'next/image';
import Link from 'next/link';
import { useFormContext } from 'react-hook-form';
import { useAccount } from 'wagmi';

import IconAlert from '@/assets/icons/icon-alert.svg';
import IconChecked from '@/assets/icons/icon-checked.svg';
import IconUnchecked from '@/assets/icons/icon-unchecked.svg';
import { Loader } from '@/components/Loader';
import { useCalculatePrincipal } from '@/hooks/loans/useCalculatePrincipal';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { changeQuickReqLoans, fetchOffersLoans, selectLoans } from '@/redux/loans/loansSlice';
import type { QuickLoanNFT } from '@/utils/divide-sort-loan-request';
import { cn } from '@/utils/helper';
import { QUICK_LOAN_LTV, WETH_TOKEN } from 'constant';
import { AlchemyNFT } from 'simplehash_api/types/simpleHash';

interface SecondStepContentProps {
  description: string;
  nfts: QuickLoanNFT[];
  showAlert?: boolean;
}

const SecondStepContent: React.FC<SecondStepContentProps> = ({ description, nfts, showAlert }) => {
  const ulRef = useRef<HTMLUListElement>(null);
  const dispatch = useAppDispatch();
  const offersLoans = useAppSelector((state) => state.loans.offersLoans);
  const offersLoansLoading = useAppSelector((state) => state.loans.offersLoansLoading);

  const userDetail = useAppSelector((state) => state.user.userDetail);
  const { address } = useAccount();
  const { requestedQuickLoan } = useAppSelector(selectLoans);
  const calculatePrincipal = useCalculatePrincipal();
  const methods = useFormContext();

  const [nextPage, setNextPage] = useState(4);
  const displayedNfts = useMemo(() => nfts?.slice(0, nextPage) || [], [nfts, nextPage]);

  const handleSelect = (token: AlchemyNFT) => {
    dispatch(changeQuickReqLoans(token));
    methods.setValue('ltv', QUICK_LOAN_LTV);
    methods.setValue('principal', calculatePrincipal(QUICK_LOAN_LTV, token.contract.openSeaMetadata.floorPrice, WETH_TOKEN.symbol));
    methods.setValue('currency', WETH_TOKEN.address);
  };

  const handleShowMore = () => {
    setNextPage((prev) => prev + 4);
  };

  useEffect(() => {
    if (ulRef.current) {
      const listItems = ulRef.current.children;
      const firstNewIndex = nextPage - 4;
      if (listItems[firstNewIndex]) {
        listItems[firstNewIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [nextPage]);

  useEffect(() => {
    if (!address) return;
    dispatch(
      fetchOffersLoans({
        address,
      })
    );
  }, [dispatch, address]);

  const filteredOffersLoans = useMemo(() => {
    return offersLoans?.filter((offer) => offer.userId !== userDetail?.id);
  }, [offersLoans, userDetail?.id]);

  return (
    <>
      <hgroup className='flex flex-col gap-1.5 items-center'>
        {showAlert && <IconAlert aria-label='Alert' />}
        <h3 className='text-lg/[25px] font-semibold dark:text-white text-coal-250 text-center'>Loan Recommendations</h3>
        <p className='text-[13px]/[17px] dark:text-white text-coal-250 text-center max-w-[320px] w-full'>{description}</p>
      </hgroup>
      <ul ref={ulRef} className='flex flex-col gap-1 max-h-72 overflow-auto custom-scrollbar'>
        {offersLoansLoading ? (
          <Loader />
        ) : (
          <>
            {displayedNfts.map((token) => {
              const { tokenId, image, name, collection, contract } = token;
              const isActive = tokenId === requestedQuickLoan?.tokenId && collection.slug === requestedQuickLoan?.collection.slug;

              const offer = filteredOffersLoans?.find((val) => String(val.tokenId) === tokenId || val.collection?.address.toLowerCase() === contract.address.toLowerCase());
              const key = `${collection.slug}-${tokenId}`;
              return (
                <li key={key}>
                  <label htmlFor={key} className={cn('size-full px-[30px] py-5 rounded-sm flex items-center justify-between gap-1 cursor-pointer transition-all ease-in duration-300', isActive ? 'bg-primary-blue-400/15 ring-1 ring-primary-blue-400' : 'dark:bg-jet-black bg-white')}>
                    <div className='flex items-center gap-[13px]'>
                      <Image width={25} height={25} quality={100} src={image.contentType === 'image/gif' ? image.thumbnailUrl : (image?.originalUrl ?? '/bayc-collectors.png')} alt={name} className='size-[25px] object-cover' />
                      <h4 className='dark:text-white text-coal-250 text-[16px]/[28px] font-semibold'>
                        {collection?.name || contract.name} #{tokenId}
                      </h4>
                    </div>
                    {offer ? (
                      <Link href={offer.isCollection ? `/loans/loan-offers/${offer.collectionId}` : `/loans/${token.tokenId}/offers`} className='w-[93px] h-[39px] text-xs/[14px] font-medium rounded bg-primary text-white flex items-center justify-center'>
                        View Offer
                      </Link>
                    ) : (
                      <div className='flex items-center gap-[11px]'>
                        <strong className='dark:text-white text-coal-250 text-[16px]/[28px] font-semibold whitespace-nowrap'>{token.percentFloorPrice.toFixed(3)} WETH</strong>
                        <input
                          type='radio'
                          id={key}
                          onChange={() => {
                            handleSelect(token);
                          }}
                          name='selectedNft'
                          checked={isActive}
                          className='hidden'
                        />
                        {isActive ? <IconChecked aria-label='Selected' /> : <IconUnchecked aria-label='Not selected' />}
                      </div>
                    )}
                  </label>
                </li>
              );
            })}
          </>
        )}
      </ul>
      {nfts.length > 4 && nextPage < nfts.length && (
        <button onClick={handleShowMore} type='button' className='mx-auto text-xs font-medium rounded text-primary-blue-100 flex justify-center items-center bg-primary-blue-400/15 w-[109px] h-[39px]'>
          Load More
        </button>
      )}
    </>
  );
};

export default SecondStepContent;
